import React, { useCallback, useRef, useState, useEffect } from "react";
import { IReturn } from "@/interfaces/Return";
import CanceledReturn from "@/components/return/CanceledReturn";
import ReturnSkeleton from "@/components/return/ReturnSkeleton";
import { DEFAULT_PAGE_SIZE } from "@/constants/constants";
import { getReturnsCanceledBySeller } from "@/services/return-list.service";

interface CanceledReturnListProps {
  sellerName: string;
  statusId: number;
  searchText: string;
  isRefreshing: boolean;
}

const CanceledReturnList: React.FC<CanceledReturnListProps> = ({
  sellerName,
  statusId,
  searchText,
  isRefreshing,
}) => {
  const [canceledReturnList, setCanceledReturnList] = useState<IReturn[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);

  // Ref for intersection observer
  const observer = useRef<IntersectionObserver>();
  const lastReturnElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPageNumber((prevPage) => prevPage + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore]
  );

  const fetchReturnsCancelled = async (page: number) => {
    try {
      setIsLoading(true);
      const response = await getReturnsCanceledBySeller({
        keyword: parseInt(searchText),
        page_number: page,
        page_size: DEFAULT_PAGE_SIZE,
        statutId: statusId,
        vendeur_libelle: sellerName,
      });
      if (response && response.data.success) {
        if (page === 1) {
          setCanceledReturnList(response.data.data.list);
        } else {
          setCanceledReturnList((prev) => [
            ...prev,
            ...response.data.data.list,
          ]);
        }
        setHasMore(response.data.data.list.length === DEFAULT_PAGE_SIZE);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setPageNumber(1);
    fetchReturnsCancelled(1);
  }, [isRefreshing, searchText, statusId, sellerName]);

  useEffect(() => {
    if (pageNumber > 1) {
      fetchReturnsCancelled(pageNumber);
    }
  }, [pageNumber]);

  if (!isLoading && canceledReturnList.length === 0) {
    return (
      <div className="text-center text-gray-500 font-sans">
        Aucune tournée annulée
      </div>
    );
  }

  return (
    <>
      {canceledReturnList.map((item, index) => (
        <div
          key={item.numTournee}
          ref={
            index === canceledReturnList.length - 1
              ? lastReturnElementRef
              : undefined
          }
        >
          <CanceledReturn return_={item} />
        </div>
      ))}
      {isLoading &&
        Array.from({ length: 3 }).map((_, index) => (
          <ReturnSkeleton key={`loading-${index}`} />
        ))}
    </>
  );
};

export default CanceledReturnList;
